import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import {TranslationReduxState} from "platform/redux/translation/TranslationReduxState";
import {
    LoadLanguagePayload, LoadLanguageType,
    SetDetectedLangCode, SetDetectedLangCodePayload,
    SetLangCode, SetLangCodePayload,
    SetSupportedLanguages, SetSupportedLanguagesPayload
} from "platform/redux/translation/TranslationActions";
import Utils from "platform/util/Utils";
import Platform from "platform/Platform";
import {LangCode} from "platform/enum/LangCode";
import {LSKey} from "platform/storage/Storage";

export default class TranslationReducer extends Reducer<TranslationReduxState> {

    private static _instance: TranslationReducer;
    private _pendingLanguages: LangCode[] = [];
    private _loading: boolean;

    private constructor() {
        super();
        this._middlewareActions.set(LoadLanguageType, this.loadLanguage);
    }

    public static instance(): TranslationReducer {
        return this._instance || (this._instance = new this());
    }

    public get name(): string {
        return "translation";
    }

    protected setup(builder: ReducerBuilder<TranslationReduxState>): void {
        builder
            .init({
                langCode: LangCode.NONE,
                supportedLanguages: Platform.config().supportedLanguages || []
            })
            .handle(SetDetectedLangCode, (state: TranslationReduxState, {payload}: Action<SetDetectedLangCodePayload>) => {
                this._logger.debug("Set detected lang code: " + payload.langCode);
                return Object.assign({}, state, {
                    detectedLangCode: payload.langCode
                });
            })
            .handle(SetLangCode, (state: TranslationReduxState, {payload}: Action<SetLangCodePayload>) => {
                this._logger.debug("Set lang code: " + payload.langCode);
                return Object.assign({}, state, {
                    langCode: payload.langCode
                });
            })
            .handle(SetSupportedLanguages, (state: TranslationReduxState, {payload}: Action<SetSupportedLanguagesPayload>) => {
                this._logger.debug("Set supported languages: " + payload.languages);
                return Object.assign({}, state, {
                    supportedLanguages: payload.languages
                });
            });
    }

    private loadLanguage = (payload: LoadLanguagePayload) => {
        if (Utils.isNotNull(payload.langCode)) {
            if (this._loading) {
                this._logger.warn(`Postpone load language: ${payload.langCode}`);
                this._pendingLanguages.push(payload.langCode);
            } else {
                this.load(payload.langCode);
            }
        } else {
            this._logger.warn("Load lang code not defined");
        }
    }

    private load = (langCode: LangCode) => {
        this._loading = true;
        this._logger.info("Load language: " + langCode);
        Platform.storage().setItem(LSKey.LangCode, langCode);
        Platform.loadTranslation(langCode)
            .then((answer: { langCode: LangCode, data: any }) => {
                Platform.dispatch(SetLangCode({
                    langCode: answer.langCode,
                    data: answer.data
                }));
            })
            .catch((error: any) => {
                this._logger.warn("Can't load translations for lang code: " + langCode + " Error: " + JSON.stringify(error));
            })
            .finally(() => {
                this._loading = false;
                if (Utils.isArrayNotEmpty(this._pendingLanguages)) {
                    this.load(this._pendingLanguages.shift());
                }
            });
    }
}
